/**
 * Responsibility: show the player the little they need to see while flying.
 *
 * Distinct from `debugOverlay.js`, which exists for tuning and shows everything. This is what ships
 * on screen, and the guiding rule is that it should say as little as possible: the experience is
 * built on an uncluttered view, and every element that draws the eye works against it.
 *
 * So there is no position, no framerate, no biome breakdown. Altitude and speed, because they are the
 * two things a pilot glances at, and a transient line for things that just happened — "Saved" — which
 * clears itself rather than accumulating.
 *
 * Owns its DOM and its styling. Contains no simulation logic; it reads a summary and writes text.
 */

import { HUD } from '../config.js';

/**
 * The lines the readout should show, given the current flight summary.
 *
 * Pure, and exported, because *what* to display is a decision worth testing while *where the pixels go*
 * is not. Keeping it separate is what lets the trim rule and the rounding be checked in Node.
 *
 * @param {object} info
 * @param {number} info.altitude - Height above the ground.
 * @param {number} info.speed - World units per second.
 * @param {{step: number, steps: number, neutralStep: number}} [info.trim]
 * @returns {Array<string>}
 */
export function formatReadout(info) {
  const lines = [`${Math.round(info.altitude)} alt    ${Math.round(info.speed)} spd`];

  /*
    Shown only when the player has moved it off neutral. A control sitting at its default does not need
    announcing, and one fewer line on screen is always the better outcome here.

    Labelled "cruise" rather than "trim". Trim is the accurate term for what the model does and is what
    the code calls it, but it is aviation jargon that tells a player nothing — the first person to fly
    this asked what it meant and could not tell it was doing anything.
  */
  if (info.trim && info.trim.step !== info.trim.neutralStep) {
    lines.push(`cruise ${info.trim.step} of ${info.trim.steps}`);
  }

  return lines;
}

/**
 * Create the HUD and attach it to the document.
 *
 * @param {object} [options]
 * @param {HTMLElement} [options.parent] - Defaults to `document.body`.
 * @returns {object}
 */
export function createHud({ parent = document.body } = {}) {
  const element = document.createElement('div');

  /*
    Bottom right, dim, and out of the way. Deliberately the opposite corner from the debug overlay so
    the two never overlap while tuning, and low enough that it sits outside where the aircraft flies.
  */
  Object.assign(element.style, {
    position: 'fixed',
    right: '18px',
    bottom: '16px',
    margin: '0',
    font: '13px/1.6 ui-monospace, "Cascadia Code", Consolas, monospace',
    color: 'rgba(150, 220, 255, 0.42)',
    textShadow: '0 0 8px rgba(0, 0, 0, 0.9)',
    textAlign: 'right',
    pointerEvents: 'none',
    whiteSpace: 'pre',
    zIndex: '10',
    // Fades rather than blinking, because a hard appearance is exactly the kind of jolt to avoid.
    transition: 'opacity 400ms ease',
  });

  /*
    The readout starts hidden and is shown only when something changes it — see `HUD.READOUT_SECONDS`. It
    therefore carries its own opacity rather than relying on the container's, which `setVisible` owns for
    the pause menu.
  */
  const readout = document.createElement('div');
  Object.assign(readout.style, { opacity: '0', transition: `opacity ${HUD.FADE_OUT_MS}ms ease` });

  const message = document.createElement('div');
  Object.assign(message.style, {
    marginTop: '4px',
    color: 'rgba(255, 209, 102, 0.75)',
    opacity: '0',
    transition: 'opacity 300ms ease',
  });

  element.append(readout, message);
  parent.appendChild(element);

  /** Seconds until the next refresh is due. Starts at 0 so the first frame fills it in. */
  let untilNextRefresh = 0;

  /** Seconds the transient message has left. */
  let messageRemaining = 0;

  /** Seconds the readout has left before it fades again. */
  let readoutRemaining = 0;

  /**
   * Bring the readout up, or restart its timer if it is already up.
   *
   * The transition duration is swapped rather than being one value, because appearing and leaving want
   * different speeds and CSS applies whatever is set at the moment the opacity changes.
   */
  function revealReadout() {
    if (readoutRemaining === 0) {
      readout.style.transition = `opacity ${HUD.FADE_IN_MS}ms ease`;
      readout.style.opacity = '1';
    }

    readoutRemaining = HUD.READOUT_SECONDS;
  }

  return {
    /**
     * Refresh the readout, at most every `HUD.INTERVAL` seconds.
     *
     * @param {number} dt - Seconds since the last call.
     * @param {object} info
     * @param {number} info.altitude - Height above the ground, not above zero: clearance is the number
     *   that means something while flying low.
     * @param {number} info.speed - World units per second.
     * @param {{step: number, steps: number}} [info.trim] - Trim position, shown only when it is not
     *   at neutral. A control sitting at its default does not need announcing.
     */
    update(dt, info) {
      messageRemaining = Math.max(0, messageRemaining - dt);
      if (messageRemaining === 0 && message.style.opacity !== '0') {
        message.style.opacity = '0';
      }

      if (readoutRemaining > 0) {
        readoutRemaining = Math.max(0, readoutRemaining - dt);

        if (readoutRemaining === 0) {
          readout.style.transition = `opacity ${HUD.FADE_OUT_MS}ms ease`;
          readout.style.opacity = '0';
        }
      }

      /*
        Nothing to refresh while it is hidden, and the caller is told not to bother gathering the numbers —
        altitude costs a ground sample. See `needsReadout`.
      */
      if (!info) return;

      untilNextRefresh -= dt;
      if (untilNextRefresh > 0) return;
      untilNextRefresh = HUD.INTERVAL;

      readout.textContent = formatReadout(info).join('\n');
    },

    /**
     * Whether the readout is on screen and therefore worth gathering numbers for.
     *
     * Exposed so the caller can skip the work rather than the HUD throwing it away: altitude needs a
     * ground sample, and the readout is hidden for most of a flight.
     *
     * @returns {boolean}
     */
    needsReadout() {
      return readoutRemaining > 0;
    },

    /**
     * Show a transient line, replacing any current one.
     *
     * Also brings the readout up. Every caller is announcing a change the player just made — speed, camera
     * tilt, a save — and the numbers beside it are the context for that change, so the two belong together.
     *
     * @param {string} text
     */
    showMessage(text) {
      message.textContent = text;
      message.style.opacity = '1';
      messageRemaining = HUD.MESSAGE_SECONDS;
      revealReadout();
    },

    /**
     * Bring the readout up without a message.
     *
     * For a change that speaks for itself on screen and needs no words beside it.
     */
    showReadout() {
      revealReadout();
    },

    /**
     * Hide or show the whole HUD.
     *
     * Used while the pause menu is open: the menu says everything, and leaving the flight readout
     * underneath it would be two things competing for the same glance.
     *
     * @param {boolean} visible
     */
    setVisible(visible) {
      element.style.opacity = visible ? '1' : '0';
    },

    /** Remove the element. */
    dispose() {
      element.remove();
    },
  };
}
